/**
 * ArticleEditorModeToggle — the article-only `Screen | Flow` editor mode
 * switch. Rendered in the Screen-mode editor header (via the dialog's
 * headerAction seam) and in the Flow-mode top bar.
 *
 * Forced-Flow guard (design FP-7, AC-009): a multi-screen document cannot be
 * represented by the single-screen editor, so the Screen option is disabled
 * with an explanatory tooltip instead of silently corrupting the flow.
 *
 * The Tooltip wraps the whole group (not the ToggleButton): a Tooltip between
 * ToggleButtonGroup and its buttons would swallow the group's cloned props.
 */

import React from 'react';
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import type { ArticleEditorMode } from '@modules/firebase-core';

export interface ArticleEditorModeToggleProps {
  mode: ArticleEditorMode;
  /** Multi-screen document → Screen option disabled with tooltip. */
  screenModeDisabled: boolean;
  /** Whole control disabled (e.g. while the mode change persists). */
  disabled?: boolean;
  onChange: (mode: ArticleEditorMode) => void;
}

export function ArticleEditorModeToggle({
  mode,
  screenModeDisabled,
  disabled = false,
  onChange,
}: ArticleEditorModeToggleProps) {
  const group = (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={mode}
      onChange={(_, next: ArticleEditorMode | null) => {
        if (next && next !== mode) onChange(next);
      }}
      aria-label="Editor mode"
      data-testid="article-editor-mode-toggle"
    >
      <ToggleButton
        value="screen"
        disabled={disabled || screenModeDisabled}
        data-testid="article-editor-mode-screen"
        sx={{ px: 1.75, py: 0.5, textTransform: 'none' }}
      >
        Screen
      </ToggleButton>
      <ToggleButton
        value="flow"
        disabled={disabled}
        data-testid="article-editor-mode-flow"
        sx={{ px: 1.75, py: 0.5, textTransform: 'none' }}
      >
        Flow
      </ToggleButton>
    </ToggleButtonGroup>
  );

  if (!screenModeDisabled) return group;

  return (
    <Tooltip title="Multi-screen article — edit in Flow">
      <span style={{ display: 'inline-flex' }}>{group}</span>
    </Tooltip>
  );
}

export default ArticleEditorModeToggle;
